import {
  BookOpen,
  Clock,
  Compass,
  ListTree,
  Repeat,
  Target,
  type LucideIcon,
} from 'lucide-react';
import type { WeaknessItemView } from '@en3/shared';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/cn';

/**
 * 薄弱项列表：按"弱"的程度降序，用条形把 score 变成可比较的长度。
 *
 * 三个决定：
 *
 * 1. **图标按 `type` 选，不按中文标签猜**。`type` 是机器可读的分类（词汇 / 句法 /
 *    速度 / 记忆 / 习惯 / 广度），标签将来可能被改文案；靠 label 包含"词"字之类的
 *    判断会在某次文案调整后静默失效。这里对未知 type 有兜底图标。
 *
 * 2. **每条必须带 evidence**。只写"词汇量不足"是没法反驳也没法行动的；
 *    后端的 evidence 里带具体数字（"每 100 词查词 12.4 次"），用户才能判断
 *    "这说的确实是我"还是"算法误会了"。
 *
 * 3. **空数组不是"没渲染"，而是"没有明显短板"**。空列表什么都不显示会让人以为
 *    加载失败；这里明确说"暂未发现明显短板"，并顺手给下一步方向（提高难度）。
 */

const TYPE_ICONS: Record<WeaknessItemView['type'], LucideIcon> = {
  vocabulary: BookOpen,
  syntax: ListTree,
  speed: Clock,
  retention: Repeat,
  consistency: Target,
  breadth: Compass,
};

/** 条形颜色按"弱度"分三档：越红越该先处理。分界值与后端生成 score 的量纲一致（0–100）。 */
function barClass(score: number): string {
  if (score >= 70) return 'bg-destructive';
  if (score >= 45) return 'bg-[var(--warning)]';
  return 'bg-primary';
}

export function WeaknessList({
  weaknesses,
  className,
}: {
  weaknesses: WeaknessItemView[];
  className?: string;
}) {
  // 后端已排过序，但页面不该依赖"另一个系统的排序恰好正确"——这里再排一次，成本可忽略
  const items = [...weaknesses].sort((a, b) => b.score - a.score);

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target aria-hidden className="size-4 text-primary" />
          薄弱项
        </CardTitle>
        {items.length > 0 && <span className="text-xs text-muted-foreground">按需要优先处理的程度排序</span>}
      </CardHeader>

      <CardBody className="space-y-4 text-sm">
        {items.length === 0 ? (
          <p className="rounded-md border border-dashed px-3 py-5 text-center text-xs text-muted-foreground">
            暂未发现明显短板。可以试着读难度更高一档的文章，或开始练复述与回译。
          </p>
        ) : (
          items.map((item) => {
            const Icon = TYPE_ICONS[item.type] ?? Target;
            const score = Math.round(item.score);
            return (
              <section key={`${item.type}-${item.label}`} className="rounded-lg border p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                  <h3 className="text-sm font-semibold">{item.label}</h3>
                  <span className="ml-auto text-xs tabular-nums text-muted-foreground">弱度 {score}</span>
                </div>

                <div
                  className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={score}
                  aria-label={`${item.label} 弱度`}
                >
                  <div className={cn('h-full rounded-full', barClass(score))} style={{ width: `${score}%` }} />
                </div>

                <p className="mt-2 text-xs text-muted-foreground">依据：{item.evidence}</p>
                <p className="mt-1.5 text-xs">
                  <span className="font-medium text-primary">建议：</span>
                  {item.advice}
                </p>
              </section>
            );
          })
        )}
      </CardBody>
    </Card>
  );
}
